import { Overlay, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { SecurityNoticeService } from 'app/modules/shared/services/security-notice.service';
import { TemplatePortal } from '@angular/cdk/portal';

import {
    Component,
    ElementRef,
    inject,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation,
    ChangeDetectionStrategy
} from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { TranslocoModule } from '@jsverse/transloco';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { AccountSelectorComponent } from 'app/modules/admin/account-selector/account-selector.component';
import { AccountSelectorService } from 'app/modules/admin/account-selector/account-selector.service';
import { AssetsSourcePipe } from 'app/modules/shared/pipes/assets.pipe';
import { CommonService } from 'app/modules/shared/services/common';
import { cloneDeep } from 'lodash';
import { Subject, takeUntil } from 'rxjs';
@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
    FuseLoadingBarComponent,
    FuseVerticalNavigationComponent,
    UserComponent,
    MatIconModule,
    MatButtonModule,
    LanguagesComponent,
    FuseFullscreenComponent,
    RouterOutlet,
    MatCheckboxModule,
    AccountSelectorComponent,
    AssetsSourcePipe,
    TranslocoModule,
    OverlayModule
]
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall!: boolean;
    navigation!: Navigation;
    user!: User;

    patients = [];

    isOpenAccountSelector = true;
    isOpenOriginNavigation = false;
    isOpenOriginHelpCenter = false;

    @ViewChild('targetElement') targetElement!: ElementRef;
    @ViewChild('overlayTemplate') overlayTemplate!: TemplateRef<any>;

    private overlay = inject(Overlay);
    private viewContainerRef = inject(ViewContainerRef);
    private overlayRef?: OverlayRef;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    private _securityNotice = inject(SecurityNoticeService);

    constructor(
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _accountSelector: AccountSelectorService,
        private _common: CommonService,
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Tell the user about the security notes (device protection, data remaining in the device's memory), once per browser (BSI O.Plat_1, O.Plat_7)
        this._securityNotice.showOnce();

        // Subscribe to the user service
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;
            });

        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                if (
                    this.user?.roles &&
                    !this.user.roles.includes('ROLE_ADMIN')
                ) {
                    const index = navigation.default.findIndex(
                        (n) => n.id === 'admin',
                    );
                    navigation.default.splice(index, 1);
                }

                this.navigation = navigation;
            });

        this._accountSelector.patients$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((patients: User[]) => {
                this.patients = patients;
                if (this.navigation) {
                    this.updateNavigation();
                }
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        // );

        const isOnboarding = localStorage.getItem('onboarding');
        if (isOnboarding) {
            setTimeout(() => {
                if (this.isScreenSmall) {
                    localStorage.removeItem('onboarding');
                    return;
                }
                const nav = document.getElementById('account-selector');
                this.targetElement = new ElementRef(nav);
                // const elementRef = new ElementRef(element); // Type is now ElementRef<HTMLElement | null>
                console.log(nav);
                // nav?.childNodes[0]?.classList.add('heighlited-box');
                // const div = nav?.childNodes[1] as HTMLElement;
                // console.log(div);
                nav?.classList.add('heighlited-box');
                this.openProgrammaticOverlay();
            }, 3000);
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name,
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    updateNavigation() {
        const navigation = cloneDeep(this.navigation);
        if (this.patients?.length) {
            this.navigation = null;
            const iOne = navigation.default.findIndex(
                (n) => n.id === 'configuration-user',
            );
            const iTwo = navigation.default[iOne].children.findIndex(
                (n) => n.id === 'configuration-user.create-new-user',
            );

            if (iTwo == -1) {
                navigation.default[iOne].children.splice(iTwo, 0, {
                    id: 'configuration-user.create-new-user',
                    title: this._common.t('CREATE-NEW-USER'),
                    type: 'basic',
                    icon: 'heroicons_outline:user-plus',
                    link: '/configuration-user/create-new-user',
                });
            }
        } else {
            this.navigation = null;
            const iOne = navigation.default.findIndex(
                (n) => n.id === 'configuration-user',
            );
            const iTwo = navigation.default[iOne].children.findIndex(
                (n) => n.id === 'configuration-user.create-new-user',
            );
            if (iTwo !== -1) {
                navigation.default[iOne].children.splice(iTwo, 1);
            }
        }

        this.navigation = navigation;
    }

    nextStep(value: string) {
        this.closeOverlay();
        const accontSelector = document.getElementById('account-selector');
        const nav = document.getElementById('navigation');
        const hc = document.getElementById('support.help-center');
        if (value === 'navigation') {
            this.isOpenAccountSelector = false;
            this.isOpenOriginNavigation = true;
            setTimeout(() => {
                console.log(nav);
                accontSelector?.classList.remove('heighlited-box');
                nav?.classList.add('heighlited-box');
                // const parent = nav.parentElement;
                // parent.style.top = '5px';
                this.targetElement = new ElementRef(nav);
                // if (this.isScreenSmall) {
                //     const nav = this.getNavigation('mainNavigation');
                //     nav.open();
                // }
                this.openProgrammaticOverlay();
            }, 1000);
        } else if (value === 'help-center') {
            this.isOpenOriginNavigation = false;
            this.isOpenOriginHelpCenter = true;
            setTimeout(() => {
                nav?.classList.remove('heighlited-box');
                hc?.classList.add('heighlited-box');

                const vn = document.getElementById(
                    'fuse-vertical-navigation-content',
                );
                console.log(hc);
                hc?.scrollIntoView();
                // const parent  = nav.parentElement;
                // parent.style.top = "5px";
                this.targetElement = new ElementRef(nav);
                // if (this.isScreenSmall) {
                //     const nav = this.getNavigation('mainNavigation');
                //     nav.open();
                // }
                this.openProgrammaticOverlay();
            }, 1000);
        } else if (value === 'done') {
            nav?.classList.remove('heighlited-box');
            accontSelector?.classList.remove('heighlited-box');
            hc?.classList.remove('heighlited-box');
            localStorage.removeItem('onboarding');
            // if (this.isScreenSmall) {
            //     const nav = this.getNavigation('mainNavigation');
            //     nav.close();
            // }
        }
    }

    skip(id: string) {
        // stop tour
        const element = document.getElementById(id);
        console.log(element);
        element?.classList.remove('heighlited-box');
        localStorage.removeItem('onboarding');
        this.closeOverlay();
    }

    openProgrammaticOverlay() {
        console.log(this.targetElement);
        // 1. Define positioning relative to the target native element
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(this.targetElement)
            .withPositions([
                {
                    originX: 'end',
                    originY: 'center',
                    overlayX: 'start',
                    overlayY: 'top',
                    //             originX: 'center',
                    // originY: 'bottom',
                    // overlayX: 'center',
                    // overlayY: 'top'
                },
            ]);

        // 2. Create the overlay reference instance
        this.overlayRef = this.overlay.create({
            positionStrategy,
            hasBackdrop: true,
            // backdropClass: 'cdk-overlay-transparent-backdrop',
            // backdropClass: 'cdk-overlay-dark-backdrop',
        });

        // 3. Close overlay cleanly on backdrop click
        this.overlayRef.backdropClick().subscribe(() => this.closeOverlay());

        // 4. Attach the template container to the overlay layout
        const portal = new TemplatePortal(
            this.overlayTemplate,
            this.viewContainerRef,
        );
        this.overlayRef.attach(portal);
    }

    closeOverlay() {
        if (this.overlayRef) {
            this.overlayRef.detach();
        }
    }

    getNavigation(name: string) {
        return this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
            name,
        );
    }
}
